/**
 * LocationEmployee Migration Utility
 * Creates LocationEmployee assignments from existing Location managers
 * Part of Phase 5: Employee Type Implementation
 */

import { API, graphqlOperation } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { listUsers, roleDefinitionsByClient, listAreas2, listLocations } from 'graphql/queries';
import { createLocationEmployee } from 'graphql/mutations';
import { getOrCreateEmployeeForUser } from './employeeMigration';

/**
 * Migrate Location managers to LocationEmployee assignments
 * Processes the gm array field from legacy StoreData to create assignments
 * @param {string} clientId - Client identifier
 * @param {Map} employeeMap - Map of userId to employeeId
 * @param {Function} onProgress - Progress callback
 * @returns {Object} Migration results
 */
export async function migrateLocationManagers(clientId, employeeMap, onProgress = () => {}) {
  const results = {
    created: 0,
    skipped: 0,
    errors: [],
    assignments: [],
    unmigratedGMs: {} // Track GMs that couldn't be migrated by location
  };

  try {
    onProgress('Fetching store data with managers...');
    
    // Fetch legacy store data which contains the gm arrays
    // Note: This needs to be updated to fetch from the correct source
    // For now, assuming we have access to store data with gm field
    const storeData = await fetchStoreDataWithGMs(clientId);
    
    if (!storeData || storeData.length === 0) {
      onProgress('No store data found to process');
      return results;
    }
    
    onProgress(`Found ${storeData.length} stores to process`);

    // Fetch all users for the client to create a username lookup map
    // IMPORTANT: Must paginate to get ALL users, not just first page
    onProgress('Loading all users for username lookup...');
    let allUsers = [];
    let nextToken = null;
    
    do {
      const userData = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { client: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const items = userData.data.listUsers?.items || [];
      allUsers = allUsers.concat(items);
      nextToken = userData.data.listUsers?.nextToken;
    } while (nextToken);
    
    const usernameToUserMap = new Map();
    allUsers.forEach(user => {
      usernameToUserMap.set(user.userName, user);
    });
    
    onProgress(`Loaded ${allUsers.length} users for lookup`);

    // Fetch role definitions for the client
    const roleData = await API.graphql(
      graphqlOperation(roleDefinitionsByClient, {
        clientId
      })
    );

    // Find the GM role from the fetched roles
    const roles = roleData.data.roleDefinitionsByClient?.items || [];
    const gmRole = roles.find(role =>
      role.roleCode === 'GM' &&
      role.assignmentLevel === 'LOCATION_LEVEL'
    );

    if (!gmRole) {
      throw new Error('GM role definition not found. Please create role definitions first.');
    }

    // Process each store and its GM array
    for (const store of storeData) {
      const locationId = store.locationId || store.id; // Handle both field names
      const locationName = store.locationName || store.shortName || store.longName;
      
      // Skip if no gm array
      if (!store.gm || !Array.isArray(store.gm) || store.gm.length === 0) {
        console.log(`Location ${locationName} has no managers in gm array`);
        results.skipped++;
        continue;
      }

      console.log(`Processing ${store.gm.length} GMs for location ${locationName}`);
      
      // Track unmigrated GMs for this location
      const unmigratedForLocation = [];
      
      // Process each GM in the array
      for (const gmUsername of store.gm) {
        try {
          // Skip empty usernames
          if (!gmUsername || gmUsername.trim() === '') {
            console.log(`  Skipping empty username for location ${locationName}`);
            continue;
          }
          
          // Find the User by username
          const user = usernameToUserMap.get(gmUsername);
          
          if (!user) {
            console.warn(`  User '${gmUsername}' not found for location ${locationName}`);
            unmigratedForLocation.push({
              username: gmUsername,
              reason: 'User not found in database'
            });
            results.errors.push({
              locationId,
              locationName,
              gmUsername,
              error: `User '${gmUsername}' not found in database`
            });
            continue; // Skip to next GM in array
          }

          // Get or create Employee for this User
          let employeeId = employeeMap.get(user.id);
          if (!employeeId) {
            employeeId = await getOrCreateEmployeeForUser(user, clientId);
            employeeMap.set(user.id, employeeId);
            console.log(`  Created new Employee for user ${gmUsername}`);
          }

          // Check if assignment already exists (to avoid duplicates)
          // This would need implementation of a check query
          
          // Create LocationEmployee assignment
          const assignmentInput = {
            clientId,
            employeeId,
            locationId,
            roleDefinitionId: gmRole.id,
            roleSnapshot: gmRole.roleName,
            isPrimary: store.gm.indexOf(gmUsername) === 0, // First GM is primary
            isActive: true,
            effectiveDate: store.lastModified || new Date().toISOString(),
            assignedBy: 'SYSTEM',
            assignmentReason: `Migrated from store.gm array (position ${store.gm.indexOf(gmUsername) + 1} of ${store.gm.length})`,
            employeeNameSnapshot: `${user.firstName || ''} ${user.lastName || ''}`.trim() || gmUsername,
            locationNameSnapshot: locationName,
            createdAt: new Date().toISOString(),
            lastModified: new Date().toISOString()
          };

          const assignmentResult = await API.graphql(
            graphqlOperation(createLocationEmployee, { input: assignmentInput })
          );
          
          results.created++;
          results.assignments.push({
            locationId,
            locationName,
            gmUsername,
            employeeId,
            assignmentId: assignmentResult.data.createLocationEmployee.id,
            isPrimary: assignmentInput.isPrimary
          });

          onProgress(`  Created assignment for ${gmUsername} at ${locationName}`);
          
        } catch (error) {
          console.error(`  Error migrating GM '${gmUsername}' for location ${locationName}:`, error);
          unmigratedForLocation.push({
            username: gmUsername,
            reason: error.message
          });
          results.errors.push({
            locationId,
            locationName,
            gmUsername,
            error: error.message
          });
        }
      }
      
      // Track unmigrated GMs for this location if any
      if (unmigratedForLocation.length > 0) {
        results.unmigratedGMs[locationName] = unmigratedForLocation;
      }
    }

    // Generate summary of unmigrated GMs
    generateUnmigratedGMsSummary(results.unmigratedGMs, onProgress);
    
    onProgress(`Migration complete: ${results.created} location assignments created`);
    return results;
    
  } catch (error) {
    console.error('Fatal error in location manager migration:', error);
    throw error;
  }
}

/**
 * Generate a detailed summary of unmigrated GMs for manual handling
 * @param {Object} unmigratedGMs - Object with location names as keys and arrays of unmigrated GMs as values
 * @param {Function} onProgress - Progress callback
 */
function generateUnmigratedGMsSummary(unmigratedGMs, onProgress) {
  const locationCount = Object.keys(unmigratedGMs).length;
  
  if (locationCount === 0) {
    onProgress('\n✅ All GMs successfully migrated!');
    return;
  }
  
  console.log('\n' + '='.repeat(80));
  console.log('UNMIGRATED GMs SUMMARY - MANUAL ACTION REQUIRED');
  console.log('='.repeat(80));
  console.log(`\nFound unmigrated GMs in ${locationCount} location(s):\n`);
  
  let totalUnmigrated = 0;
  
  // Sort locations alphabetically for easier review
  const sortedLocations = Object.keys(unmigratedGMs).sort();
  
  sortedLocations.forEach((locationName, index) => {
    const unmigrated = unmigratedGMs[locationName];
    totalUnmigrated += unmigrated.length;
    
    console.log(`${index + 1}. ${locationName} (${unmigrated.length} GM${unmigrated.length > 1 ? 's' : ''})`);
    unmigrated.forEach(gm => {
      console.log(`   - Username: "${gm.username}" | Reason: ${gm.reason}`);
    });
    console.log('');
  });
  
  console.log('-'.repeat(80));
  console.log(`TOTAL: ${totalUnmigrated} GM assignment(s) require manual review`);
  console.log('-'.repeat(80));
  
  console.log('\nACTION ITEMS:');
  console.log('1. Review the usernames listed above');
  console.log('2. Verify if these users exist with different usernames');
  console.log('3. Create missing User/Employee records if needed');
  console.log('4. Manually create LocationEmployee assignments');
  console.log('5. Update source data to correct any username mismatches');
  
  console.log('\n' + '='.repeat(80) + '\n');
  
  onProgress(`\n⚠️ ${totalUnmigrated} GM assignments could not be migrated - see console for details`);
}

/**
 * Helper function to fetch store data with gm arrays from listAreas2
 * Uses the same data source as other migration steps
 * @param {string} clientId - Client identifier
 * @returns {Array} Array of store objects with gm arrays and location mappings
 */
async function fetchStoreDataWithGMs(clientId) {
  try {
    console.log('\n📊 Fetching legacy store data from listAreas2...');
    console.log('Query parameters:', {
      report: 'store-admin',
      role: 'Admin', 
      user: 'grmAdmin'
    });
    
    // Fetch legacy data using listAreas2 - same as other migrations
    const legacyResult = await API.graphql(
      graphqlOperation(listAreas2, {
        report: 'store-admin',
        role: 'Admin',
        user: 'grmAdmin'
      })
    );
    
    const legacyData = legacyResult.data?.listAreas2 || [];
    
    // Log detailed listAreas2 output including store data
    console.log('\n📋 listAreas2 Query Results:');
    console.log(`   Total areas: ${legacyData.length}`);
    console.log('legacyResult', legacyData)
    
    let totalStores = 0;
    let storesWithGMsCount = 0;
    let totalGMAssignments = 0;
    const gmUsernameSet = new Set();
    
    // Determine if we should show detailed output (limit for large datasets)
    const MAX_DETAILED_STORES = 10; // Show details for first 10 stores
    let detailedStoreCount = 0;
    
    // Log each area with its stores
    legacyData.forEach((area, areaIndex) => {
      const areaStoreCount = area.stores?.length || 0;
      totalStores += areaStoreCount;
      
      console.log(`\n   Area ${areaIndex + 1}: ${area.areaName || area.areaId}`);
      console.log(`   └── Total stores: ${areaStoreCount}`);
      
      // Log store details for this area
      if (area.stores && area.stores.length > 0) {
        area.stores.forEach((store, storeIndex) => {
          const hasGMs = store.gm && Array.isArray(store.gm) && store.gm.length > 0;
          
          if (hasGMs) {
            storesWithGMsCount++;
            totalGMAssignments += store.gm.length;
            store.gm.forEach(username => gmUsernameSet.add(username));
          }
          
          // Log store details (limit output for large datasets)
          if (detailedStoreCount < MAX_DETAILED_STORES) {
            console.log(`       Store ${storeIndex + 1}:`);
            console.log(`       ├── storeId: ${store.storeId || 'N/A'}`);
            console.log(`       ├── shortName: ${store.shortName || 'N/A'}`);
            console.log(`       ├── longName: ${store.longName || 'N/A'}`);
            console.log(`       ├── storeNumber: ${store.storeNumber || 'N/A'}`);
            console.log(`       ├── gm array: ${hasGMs ? `[${store.gm.join(', ')}]` : 'None'}`);
            
            if (store.toastId) {
              console.log(`       ├── toastId: ${store.toastId}`);
            }
            
            // Show sample of other fields if present
            const otherFields = Object.keys(store).filter(
              key => !['storeId', 'shortName', 'longName', 'storeNumber', 'gm', 'toastId'].includes(key)
            );
            if (otherFields.length > 0) {
              console.log(`       └── other fields: ${otherFields.slice(0, 5).join(', ')}${otherFields.length > 5 ? '...' : ''}`);
            }
            
            detailedStoreCount++;
          } else if (detailedStoreCount === MAX_DETAILED_STORES) {
            console.log(`       ... (showing first ${MAX_DETAILED_STORES} stores with details, ${totalStores - MAX_DETAILED_STORES} more stores in dataset)`);
            detailedStoreCount++; // Increment to show this message only once
          }
        });
      }
    });
    
    console.log('\n📊 Summary:');
    console.log(`   Total stores: ${totalStores}`);
    console.log(`   Stores with GMs: ${storesWithGMsCount}`);
    console.log(`   Total GM assignments: ${totalGMAssignments}`);
    console.log(`   Unique GM usernames: ${gmUsernameSet.size}`);
    
    // Log sample of GM usernames for verification
    if (gmUsernameSet.size > 0) {
      const sampleGMs = Array.from(gmUsernameSet).slice(0, 5);
      console.log(`   Sample GM usernames: ${sampleGMs.join(', ')}${gmUsernameSet.size > 5 ? '...' : ''}`);
    }
    
    // Fetch existing Locations to map legacy stores to Location IDs
    console.log('\n🔍 Fetching existing Locations for mapping...');
    const locationMap = new Map();
    let nextToken = null;
    
    do {
      const result = await API.graphql(
        graphqlOperation(listLocations, {
          filter: { clientId: { eq: clientId } },
          limit: 100,
          nextToken
        })
      );
      
      const locations = result.data.listLocations?.items || [];
      locations.forEach(loc => {
        // Map by both locationNumber and locationName for flexibility
        if (loc.locationNumber) {
          locationMap.set(loc.locationNumber, loc);
        }
        locationMap.set(loc.locationName, loc);
      });
      
      nextToken = result.data.listLocations?.nextToken;
    } while (nextToken);
    
    console.log(`   Loaded ${locationMap.size} existing Locations`);
    
    // Transform legacy data to include location IDs
    const storesWithGMs = [];
    const unmappedStores = [];
    
    legacyData.forEach(area => {
      area.stores.forEach(store => {
        // Skip stores without gm arrays
        if (!store.gm || !Array.isArray(store.gm) || store.gm.length === 0) {
          return;
        }
        
        // Try to find the corresponding Location
        let location = null;
        let matchType = null;
        
        // Try matching by store number first
        if (store.storeNumber) {
          location = locationMap.get(store.storeNumber);
          if (location) matchType = 'storeNumber';
        }
        
        // If not found, try by short name
        if (!location && store.shortName) {
          location = locationMap.get(store.shortName);
          if (location) matchType = 'shortName';
        }
        
        // If not found, try by long name
        if (!location && store.longName) {
          location = locationMap.get(store.longName);
          if (location) matchType = 'longName';
        }
        
        if (!location) {
          unmappedStores.push({
            storeId: store.storeId,
            shortName: store.shortName,
            storeNumber: store.storeNumber,
            gmCount: store.gm.length,
            gms: store.gm
          });
          console.warn(`   ⚠️ Could not find Location for store: ${store.shortName || store.storeNumber || store.storeId} (${store.gm.length} GMs: ${store.gm.join(', ')})`);
          return;
        }
        
        storesWithGMs.push({
          // Location data
          locationId: location.id,
          locationName: location.locationName,
          
          // Legacy store data
          storeId: store.storeId,
          shortName: store.shortName,
          longName: store.longName,
          storeNumber: store.storeNumber,
          
          // GM array
          gm: store.gm,
          
          // Metadata
          lastModified: location.lastModified || store.lastModified,
          areaId: area.areaId,
          areaName: area.areaName
        });
      });
    });
    
    // Log final mapping summary
    console.log('\n📍 Location Mapping Results:');
    console.log(`   Successfully mapped: ${storesWithGMs.length} stores`);
    console.log(`   Failed to map: ${unmappedStores.length} stores`);
    
    if (unmappedStores.length > 0) {
      console.log('\n⚠️ Unmapped Stores (these will be skipped):');
      unmappedStores.forEach(store => {
        console.log(`   - ${store.shortName || store.storeNumber || store.storeId}: ${store.gmCount} GM(s) [${store.gms.join(', ')}]`);
      });
      
      const totalUnmappedGMs = unmappedStores.reduce((sum, store) => sum + store.gmCount, 0);
      console.log(`   Total GM assignments that will be skipped: ${totalUnmappedGMs}`);
    }
    
    console.log(`\n✅ Ready to process ${storesWithGMs.length} stores with GM assignments`);
    return storesWithGMs;
    
  } catch (error) {
    console.error('Error fetching store data with GMs:', error);
    throw error;
  }
}

/**
 * Create LocationEmployee assignment for a specific location and employee
 * @param {Object} params - Assignment parameters
 * @returns {Object} Created assignment
 */
export async function createLocationAssignment({
  clientId,
  employeeId,
  locationId,
  roleDefinitionId,
  roleName,
  isPrimary = false,
  effectiveDate = new Date().toISOString(),
  assignedBy,
  assignmentReason,
  employeeName,
  locationName
}) {
  const input = {
    clientId,
    employeeId,
    locationId,
    roleDefinitionId,
    roleSnapshot: roleName,
    isPrimary,
    isActive: true,
    effectiveDate,
    assignedBy: assignedBy || 'SYSTEM',
    assignmentReason: assignmentReason || 'Manual assignment',
    employeeNameSnapshot: employeeName,
    locationNameSnapshot: locationName,
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString()
  };

  const result = await API.graphql(
    graphqlOperation(createLocationEmployee, { input })
  );

  return result.data.createLocationEmployee;
}